import on from 'dom-event';
import select from 'dom-select';
import { set, unset, contains, toggle } from 'utils/general';
import {
  disableBodyScroll,
  enableBodyScroll,
  clearAllBodyScrollLocks,
} from 'body-scroll-lock';
import bus from 'lib/bus';

const bodyLocks = [];

export default (el) => {
  const isMobileVersion = contains(el, 'js-is-mobile');
  if (isMobileVersion) {
    enableNavMobile(el);
    initNavTabs(el);
  } else {
    enableNavDesktop(el);
  }
};

const enableCountrySelectorClick = (el, countrySelector) => {
  const showCountrySelectorPanel = () =>
    bus.$emit('show-country-selector-panel');
  on(countrySelector, 'click', showCountrySelectorPanel);
};

const enableNavMobile = (el) => {
  const links = select.all('[data-mob-accordion-button]', el);
  links.forEach((link) => {
    const submenuToggleHandler = () => submenuToggle(link, links);
    on(link, 'click', submenuToggleHandler);
  });
  const countrySelector = select('.js-country-trigger', el);
  enableCountrySelectorClick(el, countrySelector);
};

const enableNavDesktop = (el) => {
  const body = document.querySelector('body');
  const listElements = select.all(
    '.header-nav--desktop [data-header-menu-link]',
    el,
  );
  const buttons = select.all(
    '.header-nav--desktop [data-mob-accordion-button]',
    el,
  );

  const closeAllNavs = (e, button) => {
    if (e.keyCode === 27) {
      listElements.forEach((item) => {
        submenuCloseDesktop(item);
        unset(item, 'is-active');
      });
      button.focus();
    }
  };

  const handleButtonFocus = (button) => {
    const parent = button.parentElement;
    const handleNavsClosing = (e) => closeAllNavs(e, button);
    on(body, 'keyup', handleNavsClosing);
    listElements.forEach((item) => {
      if (item.getAttribute('title') !== parent.getAttribute('title')) {
        submenuCloseDesktop(item);
        unset(item, 'is-active');
      }
    });
  };

  buttons.forEach((button) => {
    const buttonFocusHandler = () => handleButtonFocus(button);
    on(button, 'focus', buttonFocusHandler);
  });

  listElements.forEach((li) => {
    const handleMouseOver = () => {
      set(li, 'is-active');
      submenuOpenDesktop(li);
    };

    const handleMouseOut = () => {
      unset(li, 'is-active');
      submenuCloseDesktop(li);
    };

    on(li, 'mouseover', handleMouseOver);
    on(li, 'mouseout', handleMouseOut);
  });

  buttons.forEach((button) => {
    const handleClick = () => {
      toggle(button.parentElement, 'is-active');
    };

    on(button, 'click', handleClick);
  });
};

/**
 * Mobile Nav methods
 */

const submenuToggle = (activeLink, links) => {
  links.forEach((link) => {
    if (link === activeLink) {
      activeLink.classList.toggle('is-active');
    } else {
      link.classList.remove('is-active');
    }
  });
};

const initNavTabs = (el) => {
  const tabs = select.all('[data-nav-tab]', el);
  const tabContent = select.all('[data-nav-tab-content]', el);

  tabs.forEach((tab, index) => {
    const tabClickHandler = () => {
      clearTabs(tabs, tabContent);
      if (index === +tab.dataset.index) {
        tabs[index].classList.add('is-active');
        tabs[index].setAttribute('aria-selected', 'true');
        tabContent[index].classList.add('is-active');
      }
    };

    on(tab, 'click', tabClickHandler);
  });
};

const clearTabs = (tabs, tabContent) => {
  tabs.forEach((tab) => {
    tab.classList.remove('is-active')
    tab.setAttribute('aria-selected', 'false');
  });
  tabContent.forEach((tab) => tab.classList.remove('is-active'));
};

/**
 * Desktop Nav methods
 */

const disableBodyScrollDesktop = (el) => {
  const scrollBarGap = window.innerWidth - document.documentElement.clientWidth;
  disableBodyScroll(el, {
    reserveScrollBarGap: true,
  });
};

const submenuOpenDesktop = (el, e = null) => {
  if (e) e.preventDefault();
  if (el) {
    const link = select('.header-nav__item-link', el);
    const overlay = select('.header-nav__subnav-overlay--desktop', el);
    const subnav = select('.js-subnav', el);
    const sublinks = el.querySelectorAll('.header-nav__sublink');
    link?.setAttribute('aria-expanded', true);
    sublinks?.forEach((el) => {
      el.setAttribute('aria-hidden', false);
      el.setAttribute('tabindex', 0);
    });
    subnav.style.visibility = 'visible';
    subnav.setAttribute('aria-hidden', false);
    subnav.setAttribute('tabindex', 0);
    el.addEventListener('focusout', handleBlur);
    overlay.addEventListener('mouseover', handleBlur);
  }
};

const handleBlur = (e) => {
  const currentTarget = e.currentTarget;
  requestAnimationFrame(() => {
    if (!currentTarget.contains(document.activeElement)) {
      submenuCloseDesktop(currentTarget);
    }
  });
};

const submenuCloseDesktop = (el, e = null) => {
  if (e) e.preventDefault();
  clearAllBodyScrollLocks();
  const elSource = el.className.includes('header-nav__subnav-overlay--desktop')
    ? el.parentElement.parentElement
    : el;
  const link = select('.header-nav__item-link', elSource);
  const subnav = el.className.includes('header-nav__subnav-overlay--desktop')
    ? el.parentElement
    : select('.js-subnav', el);
  const sublinks = elSource.querySelectorAll('.header-nav__sublink');
  link?.setAttribute('aria-expanded', false);
  elSource.classList.remove('is-active');
  sublinks?.forEach((el) => {
    el.setAttribute('aria-hidden', true);
    el.setAttribute('tabindex', '-1');
  });
};
